/**
 * Result table for the lander picker — extracted so the picker shell stays
 * inside the 150-line budget.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';

import { Checkbox } from '@/components/ui/form/controls/Checkbox';
import type { BinomLanding } from '@/hooks/binom';

export interface BinomLanderPickerTableProps {
  rows: BinomLanding[];
  loading: boolean;
  selected: Record<string, boolean>;
  onToggle: (id: string, v: boolean) => void;
  groupNameByUuid: Map<string, string>;
}

const BinomLanderPickerTable: FC<BinomLanderPickerTableProps> = ({
  rows,
  loading,
  selected,
  onToggle,
  groupNameByUuid,
}) => (
  <div className="max-h-[24rem] overflow-y-auto rounded-md border border-border/40">
    <table className="w-full text-xs">
      <thead className="sticky top-0 bg-muted/30">
        <tr className="text-left text-[10px] uppercase tracking-wide text-muted-foreground">
          <th className="w-8 px-2 py-1.5"></th>
          <th className="w-12 px-2 py-1.5">↓ ID</th>
          <th className="px-2 py-1.5">Name</th>
          <th className="w-32 px-2 py-1.5">Group</th>
          <th className="w-20 px-2 py-1.5">Language</th>
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr>
            <td colSpan={5} className="p-3 text-muted-foreground">Loading…</td>
          </tr>
        ) : rows.length === 0 ? (
          <tr>
            <td colSpan={5} className="p-3 text-muted-foreground">
              No matching landings.
            </td>
          </tr>
        ) : (
          rows.map((l) => (
            <tr key={l.id} className="border-t border-border/30 hover:bg-muted/20">
              <td className="px-2 py-1.5">
                <Checkbox
                  checked={Boolean(selected[l.id])}
                  onCheckedChange={(v) => onToggle(l.id, Boolean(v))}
                />
              </td>
              <td className="px-2 py-1.5 tabular-nums text-muted-foreground">
                {l.id}
              </td>
              <td className="truncate px-2 py-1.5">{l.name}</td>
              <td className="truncate px-2 py-1.5 text-muted-foreground">
                {groupNameByUuid.get(
                  String((l as { groupUuid?: string }).groupUuid ?? ''),
                ) ?? ''}
              </td>
              <td className="px-2 py-1.5 text-muted-foreground">
                {l.languageCode ?? ''}
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

export default BinomLanderPickerTable;
